import { api } from '../client';

export const JOB_STATUSES = ['PLANNED', 'ACTIVE', 'SUSPENDED', 'COMPLETED', 'CANCELLED'] as const;
export const JOB_TYPES = [
  'DRILLING',
  'COMPLETION',
  'WORKOVER',
  'PRODUCTION',
  'INTERVENTION',
  'OTHER',
] as const;

export type JobStatus = (typeof JOB_STATUSES)[number];
export type JobType = (typeof JOB_TYPES)[number];

/**
 * A unit of field work that material is charged to.
 *
 * Carries both sides of the reference chain: our job number, and the customer's
 * AFE, well and lease — which is what a charge has to quote to be rebilled.
 */
export interface Job {
  id: string;
  jobNumber: string;
  name: string;
  status: JobStatus;
  jobType?: string | null;
  customerName?: string | null;
  afeNumber?: string | null;
  /**
   * The well, as a record rather than five text fields repeated on every job.
   * Two jobs on the same well used to duplicate all of them and agree only by
   * luck, so "what did this well cost" was a string match.
   */
  wellId?: string | null;
  well?: {
    id: string;
    name: string;
    apiNumber: string;
    wellNumber?: string | null;
    leaseName?: string | null;
    fieldName?: string | null;
    operatorName?: string | null;
  } | null;
  rigName?: string | null;
  /** Null when nobody has said whether this job needs a permit. */
  permitsRequired?: boolean | null;
  /** Whether the job runs casing or tubing. Null means nobody has said. */
  tubularsRequired?: boolean | null;
  locationId?: string | null;
  location?: { id: string; name: string; code: string } | null;
  /** Work the job is planned to do — 5,000 ft to drill, 40 stages to pump. */
  plannedBaseMetric?: number | null;
  baseMetricUnit?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  notes?: string | null;
  /** Consumed to date, so a list row answers "what has this cost". */
  consumedCost?: number;
  _count?: { consumptionRecords: number };
}

export interface JobConsumptionSummary {
  consumption: {
    recordCount: number;
    totalCost: number;
    plannedCost: number;
    costVariance: number | null;
    byItem: Array<{
      itemId: string;
      sku: string;
      name: string;
      unit: string;
      quantity: number;
      plannedQuantity: number;
      cost: number;
    }>;
    byPhase: Array<{ phase: string; quantity: number; cost: number }>;
    /** Charges still awaiting approval — they will not reach the ERP until they are. */
    unapproved: { count: number; cost: number };
  };
  /** Spend expressed per unit of planned work — the figure the next bid uses. */
  againstPlan: {
    plannedWork: number | null;
    unit: string | null;
    costPerUnit: number | null;
    basis: string;
  };
}

export type GateStatus = 'PASSED' | 'BLOCKED' | 'NOT_ASSESSED';

export interface ReadinessGate {
  key: 'material' | 'equipment' | 'stock' | 'permits' | 'transport';
  label: string;
  status: GateStatus;
  statement: string;
  unassessedBecause?: string;
  detail?: string[];
  /** Where to go and do something about it. Absent where there is nowhere useful. */
  action?: { label: string; href: string };
}

export interface JobReadiness {
  jobNumber: string;
  verdict: GateStatus;
  blockedBy: ReadinessGate['key'] | null;
  gates: ReadinessGate[];
  confidence: 'full' | 'partial' | 'none';
}

export interface QueryJobParams {
  search?: string;
  status?: string;
  jobType?: string;
  wellName?: string;
  afeNumber?: string;
  page?: number;
  limit?: number;
}

export type CreateJobDto = Partial<Omit<Job, 'id' | 'location' | '_count' | 'consumedCost'>> & {
  jobNumber: string;
  name: string;
};

export const jobsApi = {
  async getAll(params?: QueryJobParams) {
    const response = await api.get<{
      data: Job[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/jobs', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<Job & JobConsumptionSummary>(`/jobs/${id}`);
    return response.data;
  },

  /** Whether this job can start, and what is stopping it. */
  async getReadiness(id: string) {
    const response = await api.get<JobReadiness>(`/jobs/${id}/readiness`);
    return response.data;
  },

  async getConsumption(id: string) {
    const response = await api.get<JobConsumptionSummary>(`/jobs/${id}/consumption`);
    return response.data;
  },

  async create(data: CreateJobDto) {
    const response = await api.post<Job>('/jobs', data);
    return response.data;
  },

  async update(id: string, data: Partial<CreateJobDto>) {
    const response = await api.patch<Job>(`/jobs/${id}`, data);
    return response.data;
  },

  async remove(id: string) {
    const response = await api.delete(`/jobs/${id}`);
    return response.data;
  },
};
